import { v4 as uuidv4 } from "uuid";

import {
  TechOperationAssetDtoWithoutUnitsType,
  TechOperationAssetUpsertDto,
  TechOperationNestedAssetDto,
} from "../dtos/tech-operation-asset.dto";
import { ITechOperationAssetFormData } from "../interfaces/tech-operation-asset-form-data";
import { TechOperationAssetEntityType } from "../types/tech-operation-asset-entity";

export class TechOperationAsset {
  readonly id: string;
  farmId: string;
  techOperationId: string;
  name: string;
  entityType: TechOperationAssetEntityType;
  entityId: string;
  quantity: string | number;
  quantityReal: number;
  applicationRate: number;
  unit: string;
  units: string[];
  seedUnitWeight: number | null;
  isDeleted?: boolean;
  createdAt?: number; // timestamp

  constructor(id: string = uuidv4()) {
    this.id = id;
  }

  static fromDto(assetDto: TechOperationNestedAssetDto): TechOperationAsset {
    const result = new TechOperationAsset(assetDto.id);
    result.updateFromDto(assetDto);
    return result;
  }

  /** ТМЦ из ответа `AssetsV2`, где вид ТМЦ и GUID лежат в обратных полях. */
  static fromAssetsV2Dto(
    assetDto: TechOperationAssetDtoWithoutUnitsType
  ): TechOperationAsset {
    return TechOperationAsset.fromDto({
      ...assetDto,
      entityType: assetDto.entityId,
      entityId: assetDto.entityType,
    });
  }

  get asDto(): TechOperationNestedAssetDto {
    // Only send seedUnitWeight for seed assets or when it has a meaningful value
    const shouldIncludeSeedUnitWeight =
      this.entityType === TechOperationAssetEntityType.SEED ||
      (this.seedUnitWeight != null && this.seedUnitWeight > 0);

    return {
      id: this.id,
      farmId: this.farmId,
      techOperationId: this.techOperationId,
      entityType: this.entityType,
      entityId: this.entityId,
      quantity: Number(this.quantity),
      quantityReal: Number(this.quantityReal),
      applicationRate: Number(this.applicationRate),
      unit: this.unit,
      seedUnitWeight: shouldIncludeSeedUnitWeight ? this.seedUnitWeight : null,
      name: this.name,
    };
  }

  get asUpsertDto(): TechOperationAssetUpsertDto {
    const shouldIncludeSeedUnitWeight =
      this.entityType === TechOperationAssetEntityType.SEED ||
      (this.seedUnitWeight != null && this.seedUnitWeight > 0);

    return {
      id: this.id,
      farmId: this.farmId,
      techOperationId: this.techOperationId,
      entityType: this.entityType,
      entityId: this.entityId,
      quantity: Number(this.quantity),
      quantityReal: Number(this.quantityReal),
      applicationRate: Number(this.applicationRate),
      unit: this.unit,
      seedUnitWeight: shouldIncludeSeedUnitWeight ? this.seedUnitWeight : null,
      name: this.name,
    };
  }

  updateFromDto(dto: TechOperationNestedAssetDto): TechOperationAsset {
    this.farmId = dto.farmId;
    this.techOperationId = dto.techOperationId;
    this.entityType = dto.entityType;
    this.entityId = dto.entityId;
    this.quantityReal = dto.quantityReal;
    this.applicationRate = dto.applicationRate;
    this.unit = dto.unit;
    this.seedUnitWeight = dto.seedUnitWeight;
    this.name = dto.name;
    this.createdAt = dto.createdAt;
    return this;
  }

  updateFromFormData(data: ITechOperationAssetFormData): TechOperationAsset {
    this.entityId = data.entityId;
    this.entityType = data.entityType;
    this.quantityReal = data.quantityReal;
    this.applicationRate = data.applicationRate;
    this.unit = data.unit;
    this.units = data.units;
    this.seedUnitWeight = data.seedUnitWeight;
    this.name = data.name;
    return this;
  }

  setEntityType(entityType: TechOperationAssetEntityType): TechOperationAsset {
    this.entityType = entityType;
    return this;
  }

  setFarmId(farmId: string): TechOperationAsset {
    this.farmId = farmId;
    return this;
  }

  setTechOperationId(techOperationId: string): TechOperationAsset {
    this.techOperationId = techOperationId;
    return this;
  }
}
